import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Mail, Link as LinkIcon, MessageSquare, Loader2, X, RotateCcw, CheckCircle2 } from "lucide-react";
import { supabase } from "@/lib/supabaseClient";
import { sendMedicalHistoryEmail } from "@/lib/apiClient";

// =====================================================================
// SendMedicalHistoryDialog — three-option modal for getting the form
// link to the patient.
//   • Email     → server-side via the FastAPI relay (Resend)
//   • Copy link → just the URL on the clipboard
//   • Copy SMS  → friendly preformatted message with the URL,
//                  ready to paste into the staff's own SMS app.
//                  True SMS sending is post-launch (Twilio); this
//                  copy-paste flow gets us 90% of the value today.
//
// Props:
//   open                — bool
//   onClose             — () => void
//   historyId           — uuid of the Not-Submitted row (token lives on it)
//   token               — current submit_token string
//   tokenExpiresAt      — ISO string
//   patientFirstName    — for the friendly SMS preamble
//   patientEmail        — prefilled in the email field
//   clinicName          — used in the SMS preamble + email
//   onLinkSent          — (sentVia) => void — parent reloads after each send
// =====================================================================
export default function SendMedicalHistoryDialog({
  open, onClose,
  historyId, token, tokenExpiresAt,
  patientFirstName, patientEmail, clinicName,
  onLinkSent,
}) {
  const [busy, setBusy] = useState(false);
  const [emailTo, setEmailTo] = useState(patientEmail || "");
  const [currentToken, setCurrentToken] = useState(token);
  const [currentExpires, setCurrentExpires] = useState(tokenExpiresAt);

  useEffect(() => {
    if (open) {
      setEmailTo(patientEmail || "");
      setCurrentToken(token);
      setCurrentExpires(tokenExpiresAt);
    }
  }, [open, patientEmail, token, tokenExpiresAt]);

  if (!open) return null;

  const url = `${window.location.origin}/medical-history/${currentToken || ""}`;
  const smsBody = buildSmsMessage({ patientFirstName, clinicName, url });

  async function regenerate() {
    setBusy(true);
    const { data, error } = await supabase.rpc("regenerate_medical_history_token", {
      p_history_id: historyId,
    });
    setBusy(false);
    if (error) { toast.error(error.message ?? "Could not generate a new link."); return; }
    setCurrentToken(data?.submit_token);
    setCurrentExpires(data?.token_expires_at);
    toast.success("New link generated. Old link is now invalid.");
  }

  async function markSent(sentVia) {
    await supabase.rpc("mark_medical_history_token_sent", {
      p_history_id: historyId,
      p_sent_via:   sentVia,
    });
    onLinkSent?.(sentVia);
  }

  async function handleEmail() {
    if (!emailTo || !emailTo.includes("@")) {
      toast.error("Enter a valid email.");
      return;
    }
    setBusy(true);
    const res = await sendMedicalHistoryEmail({
      to_email:           emailTo,
      patient_first_name: patientFirstName || "",
      clinic_name:        clinicName       || "Your dental clinic",
      link:               url,
      expires_at:         currentExpires,
    });
    setBusy(false);
    if (res.ok) {
      await markSent("email");
      toast.success(`Form sent to ${emailTo}.`);
      onClose?.();
    } else if (res.code === "domain_not_verified") {
      toast.warning("Email domain isn't verified — copy the link instead.");
    } else {
      toast.error(res.message ?? "Email failed. Try copy-link instead.");
    }
  }

  async function handleCopy(kind) {
    const text = kind === "sms" ? smsBody : url;
    try {
      await navigator.clipboard.writeText(text);
      await markSent(kind);
      toast.success(kind === "sms"
        ? "SMS-ready message copied — paste into your phone."
        : "Link copied to clipboard.");
      onClose?.();
    } catch {
      toast.error("Couldn't access the clipboard. Long-press the link below to copy manually.");
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
      role="dialog" aria-modal="true"
      data-testid="mh-send-dialog"
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
        <div className="px-5 py-3.5 border-b border-slate-100 flex items-center justify-between bg-amber-50">
          <h2 className="font-semibold text-slate-900 inline-flex items-center gap-2">
            <Mail className="w-4 h-4 text-amber-600" /> Send medical history form
          </h2>
          <button onClick={onClose} className="p-1 rounded hover:bg-slate-200 transition" aria-label="Close">
            <X className="w-4 h-4 text-slate-500" />
          </button>
        </div>

        <div className="p-5 space-y-5">
          <p className="text-xs text-slate-500">
            The patient gets a private link to fill out their own medical history.
            The link is single-use and expires {fmtExpires(currentExpires)}.
          </p>

          {/* ---- Email ---- */}
          <section>
            <label htmlFor="mh-send-email" className="block text-xs font-semibold text-slate-700 mb-1.5 inline-flex items-center gap-1.5">
              <Mail className="w-3.5 h-3.5" /> Send via email
            </label>
            <div className="flex items-center gap-2">
              <input
                id="mh-send-email"
                type="email"
                value={emailTo}
                onChange={(e) => setEmailTo(e.target.value)}
                placeholder="patient@example.com"
                disabled={busy}
                data-testid="mh-send-email-input"
                className="flex-1 px-3 py-2 text-sm border border-slate-200 rounded focus:outline-none focus:border-amber-400"
              />
              <button
                type="button"
                onClick={handleEmail}
                disabled={busy || !emailTo.includes("@")}
                data-testid="mh-send-email-go"
                className="px-3 py-2 text-sm font-semibold bg-amber-500 text-white rounded hover:bg-amber-600 disabled:opacity-40"
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Send"}
              </button>
            </div>
          </section>

          {/* ---- Link / SMS ---- */}
          <section className="space-y-2 pt-3 border-t border-slate-100">
            <p className="text-xs font-semibold text-slate-700">Or share the link yourself</p>
            <div className="flex items-center gap-2">
              <input
                readOnly
                value={url}
                onClick={(e) => e.target.select()}
                data-testid="mh-send-link-input"
                className="flex-1 px-3 py-2 text-xs border border-slate-200 rounded bg-slate-50 text-slate-700 truncate"
              />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => handleCopy("copy")}
                disabled={busy}
                data-testid="mh-send-copy-link"
                className="px-3 py-2 text-xs font-semibold border border-slate-200 rounded hover:bg-slate-50 disabled:opacity-40 inline-flex items-center justify-center gap-1.5"
              >
                <LinkIcon className="w-3.5 h-3.5" /> Copy link
              </button>
              <button
                type="button"
                onClick={() => handleCopy("sms")}
                disabled={busy}
                data-testid="mh-send-copy-sms"
                className="px-3 py-2 text-xs font-semibold border border-slate-200 rounded hover:bg-slate-50 disabled:opacity-40 inline-flex items-center justify-center gap-1.5"
              >
                <MessageSquare className="w-3.5 h-3.5" /> Copy for SMS
              </button>
            </div>
            <p className="text-[10px] text-slate-400 pl-0.5">
              Copy for SMS gives you a friendly preamble + the link, ready to paste into your phone's SMS app.
            </p>
          </section>

          {/* ---- Regenerate ---- */}
          <section className="pt-3 border-t border-slate-100 flex items-center justify-between">
            <p className="text-[11px] text-slate-500">
              Suspect the link leaked? Generate a new one and the old becomes invalid.
            </p>
            <button
              type="button"
              onClick={regenerate}
              disabled={busy}
              data-testid="mh-send-regenerate"
              className="text-xs font-semibold text-slate-600 hover:text-slate-900 inline-flex items-center gap-1 disabled:opacity-40"
            >
              <RotateCcw className="w-3.5 h-3.5" /> New link
            </button>
          </section>
        </div>
      </div>
    </div>
  );
}

function buildSmsMessage({ patientFirstName, clinicName, url }) {
  const greet = patientFirstName ? `Hi ${patientFirstName},` : "Hi,";
  return `${greet}\n\n${clinicName || "Your dental clinic"} would like you to fill out your medical history before your visit. Please tap the link below — it takes about 5 minutes.\n\n${url}\n\nThank you!`;
}

function fmtExpires(ts) {
  if (!ts) return "soon";
  try {
    return `on ${new Date(ts).toLocaleDateString(undefined, { dateStyle: "medium" })}`;
  } catch { return "soon"; }
}
